import React from "react";
import {
  Tag,
  Avatar,
  Modal,
  Button,
  InputNumber,
} from "antd";
import {
  property as propertyApi,
  recommend as recommendApi,
} from "../../apis";
import {
  PageContainer,
  PageHeader,
  PageContent,
} from "../../components/Layout";
import { MyTable, RowAction } from "../../components";
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { ExclamationCircleOutlined, PictureOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import moment from "moment";
import useFetch from "../../hooks/useFetch";
import { useTranslate } from "../../hooks";
import { postCode, renderUser, yen } from "../../utils";

export default () => {
  const history = useHistory();
  const { id } = useParams();
  const myTableRef = React.useRef();
  const { translate } = useTranslate();
  const { isMobile, user } = useSelector((state) => state.general);
  const [sortModal, setSortModal] = React.useState({ visible: false, record: null, value: 0 });
  const [recommend] = useFetch(() => recommendApi.get(id))({});

  const [filters] = React.useState({
    recommend: id,
    ownerType: "ADMIN",
  });
  const onSortSave = async () => {
    await propertyApi.updateSort(sortModal.record._id, {
      sort     : sortModal.value,
      recommend: id,
    });
    setSortModal({ visible: false, record: null, value: 0 });
    myTableRef.current.reload();
  };
  const columns = useHeader({
    translate,
    user,
    onAction: async (key, record) => {
      switch (key) {
        case "sort_edit": {
          setSortModal({ visible: true, record, value: record.prSort ?? 0 });
          break;
        }
        case "remove_recommend": {
          Modal.confirm({
            title  : translate("system.list.model.title", "Confirm"),
            icon   : <ExclamationCircleOutlined />,
            content: translate(
              "recommend.detail.modal.remove.content",
              "Would you like to remove this space from the recommendation?"
            ),
            okText    : translate("system.action.remove", "Remove"),
            cancelText: translate("system.list.modal.canceltext", "Cancel"),
            onOk      : async () => {
              await propertyApi.unrecommend(record._id, {
                recommend: id,
              });
              myTableRef.current.reload();
            },
          });
          break;
        }
        default:
      }
    },
  });

  return (
    <PageContainer>
      <PageHeader
        title={
          <span>
            {recommend.name}
          </span>
        }
      />
      <PageContent>
        <MyTable
          bordered={!isMobile}
          ref={myTableRef}
          columns={columns}
          filters={filters}
          loadData={propertyApi.list}
          rowKey={(record) => record?.rowKey}
        />
      </PageContent>
      <Modal
        title={translate("recommend.detail.action.sort", "Edit sort")}
        visible={sortModal.visible}
        onOk={onSortSave}
        onCancel={() => setSortModal({ visible: false, record: null, value: 0 })}
      >
        <InputNumber
          min={0}
          value={sortModal.value}
          onChange={(val) => setSortModal((prev) => ({ ...prev, value: val }))}
          style={{ width: "100%" }}
        />
      </Modal>
    </PageContainer>
  );
};

const useHeader = ({ onAction, translate, user }) => {
  return [
    {
      title : "№",
      width : "20px",
      render: (r, i, count) => count + 1,
    },
    {
      title : translate("system.property.list.image", "Image"),
      render: (record) => {
        return (
          <Avatar
            src={`${record.image}`}
            shape="square"
            icon={<PictureOutlined />}
          />
        );
      },
    },
    {
      title : translate("system.useheader.title.space.name", "Name"),
      render: (record) => {
        return (
          <div style={{ minWidth: 150 }}>
            <p>{record.name}</p>
          </div>
        );
      },
      sorter: true,
    },
    {
      title : translate("system.useehader.title.sort", "表示順"),
      render: (record) => {
        return <Tag>{record?.prSort ?? "表示順を登録されていません"}</Tag>;
      },
    },
    {
      title : translate("system.property.list.address", "Address"),
      render: (record) => {
        if (!record.address) return <div>-</div>;
        return (
          <Tag style={{ whiteSpace: "pre" }}>
            {postCode(record?.address?.postCode)}
            {record?.address?.city}
            {record?.address?.address}
            {"\n"}
            {record?.address?.buildingName}
          </Tag>
        );
      },
    },
    {
      title : translate("system.useheader.title.day.taxexcluded", "Day price"),
      render: (record) => {
        if (record.negotiable) {
          return (
            <Tag>{translate("app.property.detail.negotiable", "Negotiable")}</Tag>
          );
        }
        if (record.useMultiPrice) {
          if (record.useSpecialPrice) {
            return (
              <div>
                <Tag style={{ marginBottom: "2px" }}>
                  {translate("property.list.tag.week", "Week")} {`${yen(record.weekPrice)}`}
                </Tag>
                <Tag style={{ marginBottom: "2px" }}>
                  {translate("property.list.tag.weekend", "Weekend")} {`${yen(record.weekendPrice)}`}
                </Tag>
                <Tag>
                  {translate("property.list.tag.special", "Special")}{`${yen(record.specialPrice)}`}
                </Tag>
              </div>
            );
          }
          return (
            <div>
              <Tag style={{ marginBottom: "2px" }}>
                {translate("property.list.tag.week", "Week")} {`${yen(record.weekPrice)}`}
              </Tag>
              <Tag style={{ marginBottom: "2px" }}>
                {translate("property.list.tag.weekend", "Weekend")} {`${yen(record.weekendPrice)}`}
              </Tag>
            </div>
          );
        }
        return (
          <Tag style={{ marginBottom: "2px" }}>{`${yen(record.weekPrice)}`}</Tag>
        );
      },
      sorter: true,
    },
    {
      title : translate("system.useheader.title.status", "Status"),
      render: (record) => {
        return (
          <Tag>{translate(record.propertyStatus, record.propertyStatus)}</Tag>
        );
      },
      sorter: true,
    },
    {
      title : translate("app.property.detail.date", "ステータス更新日"),
      render: (record) => {
        return (
          <div>{moment(record.propertyStatusDate).format("YYYY/MM/DD HH:mm")}</div>
        );
      },
      sorter: true,
    },
    {
      title : translate("system.useheader.title.action", "Action"),
      width : 100,
      render: (record) => {
        return (
          <RowAction
            actions={{
              sort_edit: translate(
                "recommend.detail.action.sort",
                "表示順修正"
              ),
              remove_recommend: translate(
                "recommend.detail.action.remove",
                "おすすめ一覧から外す"
              ),
            }}
            onClick={(key) => onAction(key, record)}
          />
        );
      },
    },
  ];
};